import { consumerApi } from "./consumerApi";
import { WINDOWS_RELEASE_EVIDENCE } from "@/config/secureShieldWindowsStorefront";
import {
  authorizeSecureMacProDownload,
  fetchConsumerCatalog,
  startFortressProAddonCheckout,
  startSecureMacProCheckout,
  startSecureShieldCheckout,
} from "./consumerCommerce";

const PRODUCTS = new Set(["secureshield", "securemac-pro"]);

export function isWindowsRelease(release, product) {
  if (!PRODUCTS.has(product) || release?.platform !== "windows" || release.product !== product) return false;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(release.release_id || "")) return false;
  if (!/^[0-9a-f]{64}$/i.test(release.artifact_sha256 || "") || !Number.isSafeInteger(release.size_bytes) || release.size_bytes <= 0) return false;
  if (!Array.isArray(release.architectures) || !release.architectures.includes("x64")) return false;
  if (!/^[A-Za-z0-9._-]+\.msi$/.test(release.filename || "")) return false;
  try {
    const url = new URL(release.public_update_url);
    const path = `/v1/releases/windows/${product}/${release.release_id}/${release.artifact_sha256}/${release.filename}`;
    return url.protocol === "https:" && url.hostname === "api.lightningflow.ai" && !url.port
      && !url.username && !url.password && !url.search && !url.hash && url.pathname === path;
  } catch { return false; }
}

export async function fetchWindowsRelease(product) {
  if (!PRODUCTS.has(product)) throw new Error("Unknown Windows product");
  const { data } = await consumerApi.get(`/v1/releases/${product}/latest`, { params: { platform: "windows" } });
  // Older APIs ignore unknown queries and return a Mac release. Never turn that
  // response, or shared SKU pricing, into a Windows download or sale.
  if (!isWindowsRelease(data, product)) throw new Error("This Windows edition is not available yet.");
  return data;
}

export async function downloadWindowsRelease(product) {
  const latest = await fetchWindowsRelease(product);
  return authorizeSecureMacProDownload(latest.release_id);
}

export function hasApprovedWindowsRelease(product) {
  return isWindowsRelease(WINDOWS_RELEASE_EVIDENCE[product], product);
}

export function windowsSaleOpen(product, catalog) {
  if (!hasApprovedWindowsRelease(product)) return false;
  const item = catalog?.products?.find((entry) => entry.key === product);
  return catalog?.ready === true && item?.enabled === true && item.delivery_enabled === true
    && item.verification_status === "ready" && item.currency?.toLowerCase() === "usd"
    && item.unit_amount === (product === "secureshield" ? 499 : 1999) && item.purchase_mode === "one_time";
}

export async function startWindowsCheckout(product, options = {}) {
  if (!hasApprovedWindowsRelease(product)) throw new Error("This Windows edition is not available yet.");
  const catalog = await fetchConsumerCatalog();
  if (!windowsSaleOpen(product, catalog)) throw new Error("Windows checkout is temporarily unavailable.");
  if (product === "securemac-pro" && options.haloSubscriber) {
    return startFortressProAddonCheckout({ ...options, platform: "windows" });
  }
  if (product === "secureshield") return startSecureShieldCheckout(catalog, { platform: "windows" });
  return startSecureMacProCheckout({ platform: "windows" });
}
